import { Text } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { type EmergencyCreditStatsCommon } from "@src/appV2/ClipboardScore/api/useGetClipboardScoreProfile";
import { Button } from "@src/appV2/redesign/components/Button";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { CancellationCreditProgressBar } from "@src/appV2/redesign/EmergencyCredits/components/CancellationCreditProgressBar";
import pluralize from "pluralize";

export interface ShiftCancelPageConfirmedData {
  finalScore: number;
  isWithinECWindow: boolean;
  finalECStats?: EmergencyCreditStatsCommon;
}

export interface ShiftCancelPageConfirmedHandlers {
  onContinue: () => void;
}

type ShiftCancelPageConfirmationScreenProps = ShiftCancelPageConfirmedData &
  ShiftCancelPageConfirmedHandlers;

export function ShiftCancelPageConfirmationScreen(
  props: ShiftCancelPageConfirmationScreenProps
): JSX.Element {
  const { finalScore, isWithinECWindow, finalECStats, onContinue } = props;

  const showCreditProgress =
    isWithinECWindow && isDefined(finalECStats) && finalECStats.totalAvailableCredits === 0;

  const shiftsUntilNextCredit = showCreditProgress
    ? finalECStats.requiredShiftsForNextCredit - finalECStats.completedShiftsTowardsNextCredit
    : 0;

  return (
    <Stack textAlign="center" flex={1}>
      <Stack flex={1} spacing={12} sx={{ paddingTop: 12, paddingX: 8 }}>
        <Text
          variant="h2"
          color={(theme) => theme.palette.intent?.error.text}
          sx={{ textAlign: "center" }}
        >
          This shift has been cancelled
        </Text>
        <Stack spacing={6}>
          <Text variant="body2" sx={(theme) => ({ color: theme.palette.grey[600] })}>
            Your new Clipboard Score
          </Text>
          <Text
            color={(theme) => theme.palette.text.primary}
            sx={{
              fontSize: "7.25rem",
              fontWeight: 300,
              lineHeight: "96px",
              letterSpacing: "-0.01em",
            }}
          >
            {finalScore}
          </Text>
        </Stack>

        {showCreditProgress && (
          <Stack spacing={6}>
            <Text semibold variant="body1">
              Complete {shiftsUntilNextCredit} more {pluralize("shift", shiftsUntilNextCredit)} to
              earn back credit
            </Text>
            <CancellationCreditProgressBar cancellationCreditStats={finalECStats} />
          </Stack>
        )}
      </Stack>

      <DialogFooter orientation="vertical">
        <Button fullWidth variant="contained" size="large" onClick={onContinue}>
          Got it
        </Button>
      </DialogFooter>
    </Stack>
  );
}
